import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import WhatsAppStatusDot from "../common/WhatsAppStatusDot";
import VoiceStatusDot from "../common/VoiceStatusDot";
import NotificationCenter from "../common/NotificationCenter";
import { BRAND } from "../../branding";
import { useResponsiveLayout } from "../responsive-fixes/useResponsiveLayout";
import HeaderMobilePanel from "../responsive-fixes/HeaderMobilePanel";

interface HeaderProps {
  user?: { name: string; role: string; phone: string };
  onLogout?: () => void;
  onToggleSidebar?: () => void;
  sidebarExpanded?: boolean;
}

interface RoleInfo {
  label: string;
  icon: string;
  color: string;
}

const ROLE_LABELS: Record<string, RoleInfo> = {
  super_admin: { label: "Super Admin", icon: "âš¡", color: "#ffd6a5" },
  owner: { label: "Owner", icon: "ðŸ‘‘", color: "#ffd6a5" },
  general_manager: { label: "General Manager", icon: "ðŸ›ï¸", color: "#c8bfff" },
  tech_shareholder: { label: "Tech Shareholder", icon: "âš™ï¸", color: "#c8bfff" },
  shareholder: { label: "Shareholder", icon: "ðŸ’¼", color: "#c8bfff" },
  consultant: { label: "Consultant", icon: "ðŸ¤", color: "#cfeda2" },
  accountant: { label: "Accountant", icon: "ðŸ“Š", color: "#cfeda2" },
  labor: { label: "Operations", icon: "ðŸ”§", color: "#cfeda2" },
  employee: { label: "Employee", icon: "ðŸ‘¤", color: "#cfeda2" },
};

const Header: React.FC<HeaderProps> = ({ 
  user, 
  onLogout, 
  onToggleSidebar,
  sidebarExpanded = false 
}) => {
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const currentPath = location.pathname;
  const responsive = useResponsiveLayout();

  const roleInfo = ROLE_LABELS[user?.role || "employee"] || ROLE_LABELS["employee"];
  const firstName = user?.name?.split(" ")[0] || "User";

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.user-menu-container') && userMenuOpen) {
        setUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [userMenuOpen]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && userMenuOpen) {
        setUserMenuOpen(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [userMenuOpen]);

  const handleLogout = () => {
    setUserMenuOpen(false);
    if (onLogout) {
      onLogout();
    } else {
      // Default logout behavior
      fetch("/api/auth/logout", { method: "POST" })
        .then(() => {
          window.location.href = "/login";
        })
        .catch(console.error);
    }
  };

  const quickNavItems = [
    { href: "/ai", label: "AI Center", icon: "psychology" },
    { href: "/crm", label: "CRM", icon: "contacts" },
    { href: "/whatsapp", label: "WhatsApp", icon: "chat" },
    { href: "/accounting", label: "AfaqBooks", icon: "receipt_long" },
  ];

  return (
    <header 
      className="afaq-header" 
      style={{
        background: "rgba(6,14,32,0.7)",
        backdropFilter: "blur(40px)",
        WebkitBackdropFilter: "blur(40px)",
        borderBottom: "0.5px solid rgba(189,178,255,0.08)",
        boxShadow: "0 0 32px rgba(189,178,255,0.04)",
        position: "sticky",
        top: 0,
        zIndex: 100,
      }}
    >
      <div className="header-container" style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "12px 24px",
        maxWidth: "100%",
      }}>
        {/* Left: Logo + Sidebar Toggle */}
        <div className="header-logo" style={{ display: "flex", alignItems: "center", gap: 16 }}>
          {/* Sidebar Toggle */}
          <button
            onClick={onToggleSidebar}
            style={{
              background: "rgba(189, 178, 255, 0.08)",
              border: "0.5px solid rgba(189, 178, 255, 0.15)",
              borderRadius: 10,
              width: 40,
              height: 40,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              color: "#bdb2ff",
              transition: "all 0.2s",
            }}
            aria-label={sidebarExpanded ? "Collapse sidebar" : "Expand sidebar"}
          >
            <span className="material-symbols-outlined" style={{ fontSize: 20 }}>
              {sidebarExpanded ? "menu_open" : "menu"}
            </span>
          </button>

          {/* Logo */}
          <a 
            href="/" 
            style={{ 
              textDecoration: "none", 
              display: "flex", 
              alignItems: "center", 
              gap: 12 
            }}
          >
            <img
              src="/brand/afaq-logo.png"
              alt="AFAQ Alnaseem"
              className="logo-img"
              style={{ height: 36, width: "auto" }}
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = "none";
              }}
            />
            <div>
              <div style={{ 
                fontFamily: BRAND.fontFamily, 
                fontWeight: 900, 
                fontSize: 18, 
                color: "#bdb2ff", 
                letterSpacing: "-0.05em",
                lineHeight: 1.2,
              }}>
                {BRAND.name}
              </div>
              <div style={{
                fontFamily: BRAND.fontFamily,
                fontSize: 10,
                color: "rgba(221, 229, 255, 0.4)",
                letterSpacing: "0.05em",
                textTransform: "uppercase",
              }}>
                Enterprise Intelligence
              </div>
            </div>
          </a>
        </div>

        {/* Center: Quick Nav */}
        <div className="header-nav" style={{ 
          display: "flex", 
          alignItems: "center", 
          gap: 6,
          flex: 1,
          justifyContent: "center",
          maxWidth: 600,
        }}>
          {quickNavItems.map(({ href, label, icon }) => {
            const isActive = currentPath === href || currentPath.startsWith(href + "/");
            return (
              <a 
                key={href} 
                href={href} 
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  fontFamily: BRAND.fontFamily,
                  fontWeight: 700,
                  fontSize: 12,
                  textTransform: "uppercase",
                  letterSpacing: "0.08em",
                  color: isActive ? "#bdb2ff" : "rgba(221,229,255,0.55)",
                  textDecoration: "none",
                  padding: "8px 16px",
                  borderRadius: 20,
                  border: isActive 
                    ? "0.5px solid rgba(189, 178, 255, 0.4)" 
                    : "0.5px solid rgba(189,178,255,0.1)",
                  background: isActive ? "rgba(189,178,255,0.1)" : "transparent",
                  transition: "all 0.2s",
                  whiteSpace: "nowrap",
                }}
                onMouseEnter={e => {
                  if (!isActive) {
                    (e.currentTarget as HTMLAnchorElement).style.color = "#bdb2ff";
                    (e.currentTarget as HTMLAnchorElement).style.borderColor = "rgba(189,178,255,0.3)";
                    (e.currentTarget as HTMLAnchorElement).style.background = "rgba(189,178,255,0.05)";
                  }
                }}
                onMouseLeave={e => {
                  if (!isActive) {
                    (e.currentTarget as HTMLAnchorElement).style.color = "rgba(221,229,255,0.55)";
                    (e.currentTarget as HTMLAnchorElement).style.borderColor = "rgba(189,178,255,0.1)";
                    (e.currentTarget as HTMLAnchorElement).style.background = "transparent";
                  }
                }}
              >
                <span className="material-symbols-outlined" style={{ fontSize: 16 }}>{icon}</span>
                {label}
              </a>
            );
          })}
        </div>

        {/* Right: Status + User */}
        <div className="header-account" style={{ 
          display: "flex", 
          alignItems: "center", 
          gap: 16,
        }}>
          {/* Status Indicators — M8 fix (audit PR #51): on phone we
              wrap each dot in a 44x44 hit zone so tap targets meet
              Apple HIG / WCAG 2.5.5. Visuals unchanged on desktop. */}
          <div style={{ display: "flex", alignItems: "center", gap: responsive.isPhone ? 2 : 8 }}>
            <span style={{
              display: "flex", alignItems: "center", justifyContent: "center",
              minWidth: responsive.isPhone ? 44 : undefined,
              minHeight: responsive.isPhone ? 44 : undefined,
              touchAction: "manipulation",
            }}>
              <WhatsAppStatusDot />
            </span>
            <span style={{
              display: "flex", alignItems: "center", justifyContent: "center",
              minWidth: responsive.isPhone ? 44 : undefined,
              minHeight: responsive.isPhone ? 44 : undefined,
              touchAction: "manipulation",
            }}>
              <VoiceStatusDot />
            </span>
          </div>

          {/* Notifications — M8: 44x44 hit zone wrapping the bell on phone. */}
          <span style={{
            display: "flex", alignItems: "center", justifyContent: "center",
            minWidth: responsive.isPhone ? 44 : undefined,
            minHeight: responsive.isPhone ? 44 : undefined,
            touchAction: "manipulation",
          }}>
            <NotificationCenter />
          </span>

          {/* Settings */}
          <a 
            href="/integrations" 
            style={{ 
              color: "rgba(189,178,255,0.6)", 
              display: "flex",
              padding: 8,
              borderRadius: 8,
              transition: "all 0.2s",
            }}
            onMouseEnter={e => {
              (e.currentTarget as HTMLAnchorElement).style.color = "#bdb2ff";
              (e.currentTarget as HTMLAnchorElement).style.background = "rgba(189, 178, 255, 0.08)";
            }}
            onMouseLeave={e => {
              (e.currentTarget as HTMLAnchorElement).style.color = "rgba(189,178,255,0.6)";
              (e.currentTarget as HTMLAnchorElement).style.background = "transparent";
            }}
          >
            <span className="material-symbols-outlined" style={{ fontSize: 20 }}>settings</span>
          </a>

          {/* User Menu */}
          {user && (
            <div className="user-menu-container" style={{ position: "relative" }}>
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                style={{
                  background: "rgba(15,25,47,0.6)",
                  border: "0.5px solid rgba(189,178,255,0.2)",
                  borderRadius: 12,
                  padding: "8px 14px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  fontFamily: BRAND.fontFamily,
                  color: "#dde5ff",
                  fontSize: 13,
                  backdropFilter: "blur(10px)",
                  transition: "all 0.2s",
                }}
                onMouseEnter={e => {
                  (e.currentTarget as HTMLButtonElement).style.borderColor = "rgba(189,178,255,0.4)";
                  (e.currentTarget as HTMLButtonElement).style.background = "rgba(15,25,47,0.8)";
                }}
                onMouseLeave={e => {
                  (e.currentTarget as HTMLButtonElement).style.borderColor = "rgba(189,178,255,0.2)";
                  (e.currentTarget as HTMLButtonElement).style.background = "rgba(15,25,47,0.6)";
                }}
                aria-expanded={userMenuOpen}
                aria-haspopup="true"
              >
                <span>{roleInfo.icon}</span>
                <span style={{ fontWeight: 600 }}>{firstName}</span>
                <span style={{
                  background: `${roleInfo.color}18`,
                  color: roleInfo.color,
                  fontSize: 10,
                  padding: "3px 8px",
                  borderRadius: 99,
                  fontWeight: 700,
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  border: `1px solid ${roleInfo.color}30`,
                }}>
                  {roleInfo.label}
                </span>
                <span className="material-symbols-outlined" style={{ 
                  fontSize: 16, 
                  color: "rgba(221,229,255,0.3)",
                  transform: userMenuOpen ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "transform 0.2s",
                }}>
                  expand_more
                </span>
              </button>

              {/* Dropdown — desktop. Phone uses HeaderMobilePanel below
                  (M7 audit fix: dropdown becomes a full-width slide-down
                  panel under the header on phone). */}
              {userMenuOpen && !responsive.isPhone && (
                <div style={{
                  position: "absolute",
                  top: "calc(100% + 8px)",
                  right: 0,
                  background: "rgba(9,19,39,0.98)",
                  backdropFilter: "blur(30px)",
                  border: "0.5px solid rgba(189,178,255,0.15)",
                  borderRadius: 16,
                  padding: 8,
                  minWidth: 240,
                  zIndex: 100,
                  boxShadow: "0 20px 60px rgba(0,0,0,0.5), 0 0 20px rgba(189,178,255,0.05)",
                  animation: "fadeIn 0.2s ease-out",
                }}>
                  {/* User Info */}
                  <div style={{ 
                    padding: "16px 14px 14px", 
                    borderBottom: "0.5px solid rgba(189,178,255,0.08)", 
                    marginBottom: 8 
                  }}>
                    <div style={{ 
                      color: "#dde5ff", 
                      fontWeight: 700, 
                      fontFamily: BRAND.fontFamily, 
                      fontSize: 15,
                      marginBottom: 4,
                    }}>
                      {user.name}
                    </div>
                    <div style={{ 
                      color: "rgba(221,229,255,0.4)", 
                      fontSize: 12, 
                      fontFamily: BRAND.fontFamily,
                      marginBottom: 8,
                    }}>
                      +{user.phone}
                    </div>
                    <div style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      background: "rgba(189,178,255,0.06)",
                      borderRadius: 8,
                      padding: "6px 10px",
                    }}>
                      <span style={{ fontSize: 14 }}>{roleInfo.icon}</span>
                      <span style={{
                        fontFamily: BRAND.fontFamily,
                        fontSize: 11,
                        fontWeight: 700,
                        color: roleInfo.color,
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                      }}>
                        {roleInfo.label}
                      </span>
                    </div>
                  </div>

                  {/* Menu Items */}
                  {[
                    { href: "/profile", icon: "manage_accounts", label: "My Profile" },
                    { href: "/admin", icon: "shield", label: "Admin Panel", roles: ["super_admin", "owner", "tech_shareholder"] },
                    { href: "/my-dashboard", icon: "dashboard", label: "My Dashboard" },
                    { href: "/help", icon: "help", label: "Help Centre" },
                  ].map(({ href, icon, label, roles }) => {
                    if (roles && !roles.includes(user.role)) return null;
                    
                    return (
                      <a 
                        key={href} 
                        href={href} 
                        onClick={() => setUserMenuOpen(false)} 
                        style={{
                          display: "flex", 
                          alignItems: "center", 
                          gap: 12,
                          width: "100%", 
                          padding: "10px 14px",
                          borderRadius: 10, 
                          cursor: "pointer",
                          color: "#dde5ff", 
                          textDecoration: "none",
                          fontFamily: BRAND.fontFamily, 
                          fontSize: 13,
                          transition: "background 0.15s",
                          fontWeight: 500,
                        }}
                        onMouseEnter={e => (e.currentTarget.style.background = "rgba(189,178,255,0.08)")}
                        onMouseLeave={e => (e.currentTarget.style.background = "transparent")}
                      >
                        <span className="material-symbols-outlined" style={{ fontSize: 18, color: "#bdb2ff" }}>{icon}</span>
                        {label}
                      </a>
                    );
                  })}

                  {/* Logout */}
                  <div style={{ borderTop: "0.5px solid rgba(189,178,255,0.08)", marginTop: 8, paddingTop: 8 }}>
                    <button
                      onClick={handleLogout}
                      style={{
                        width: "100%", 
                        padding: "10px 14px",
                        background: "none", 
                        border: "none",
                        borderRadius: 10, 
                        cursor: "pointer",
                        display: "flex", 
                        alignItems: "center", 
                        gap: 12,
                        color: "#ff6b6b",
                        fontFamily: BRAND.fontFamily, 
                        fontSize: 13,
                        textAlign: "left",
                        transition: "background 0.15s",
                        fontWeight: 500,
                      }}
                      onMouseEnter={e => (e.currentTarget.style.background = "rgba(255,107,107,0.08)")}
                      onMouseLeave={e => (e.currentTarget.style.background = "none")}
                    >
                      <span className="material-symbols-outlined" style={{ fontSize: 18 }}>logout</span>
                      Sign out
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* M7 fix (audit PR #51): on phone the user-menu dropdown becomes
          a full-width slide-down panel anchored under the header. The
          desktop absolute-positioned dropdown above is suppressed when
          responsive.isPhone is true (see condition above). */}
      {user && responsive.isPhone && (
        <HeaderMobilePanel
          open={userMenuOpen}
          onClose={() => setUserMenuOpen(false)}
          user={user}
          roleInfo={roleInfo}
          items={[
            { href: "/profile", icon: "manage_accounts", label: "My Profile" },
            { href: "/admin", icon: "shield", label: "Admin Panel", roles: ["super_admin", "owner", "tech_shareholder"] },
            { href: "/my-dashboard", icon: "dashboard", label: "My Dashboard" },
            { href: "/help", icon: "help", label: "Help Centre" },
          ]}
          onLogout={handleLogout}
        />
      )}
    </header>
  );
};

export default Header;


